import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useChat, type ChatContext } from "../hooks/useChat";
import { useCurrentUser } from "../lib/hooks";
import Spinner from "./Spinner";

interface ChatPanelProps {
  context?: ChatContext;
  isOpen: boolean;
  onToggle: () => void;
}

function formatToolName(tool: string): string {
  const names: Record<string, string> = {
    fetch_pdb_metadata: "PDB Lookup",
    fetch_uniprot_info: "UniProt",
    search_pdb: "PDB Search",
  };
  return names[tool] || tool;
}

export default function ChatPanel({ context, isOpen, onToggle }: ChatPanelProps) {
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { data: user } = useCurrentUser();

  const { messages, isLoading, error, activeTool, sendMessage, cancel, clearMessages } =
    useChat({ context });

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const message = inputValue;
    setInputValue("");
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
    if (e.key === "Escape") {
      onToggle();
    }
  };

  return (
    <>
      {/* Toggle button - always visible */}
      <button
        onClick={onToggle}
        className={`fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-teal-600 hover:bg-teal-700 text-white p-3 rounded-l-lg shadow-lg transition-all ${
          isOpen ? "translate-x-full opacity-0" : "translate-x-0 opacity-100"
        }`}
        title="Ask Claude about this structure"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
          />
        </svg>
      </button>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      {/* Slide-out panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[400px] lg:w-[450px] bg-slate-800 border-l border-slate-700 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-800/95 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-sm font-semibold text-white">Ask Claude</h2>
              <p className="text-xs text-slate-400">About this structure</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {messages.length > 0 && (
              <button
                onClick={clearMessages}
                className="text-xs text-slate-400 hover:text-slate-300 px-2 py-1 rounded hover:bg-slate-700 transition-colors"
              >
                Clear
              </button>
            )}
            <button
              onClick={onToggle}
              className="text-slate-400 hover:text-slate-300 p-1.5 rounded hover:bg-slate-700 transition-colors"
              title="Close (Esc)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ height: "calc(100% - 140px)" }}>
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-6">
              <div className="w-16 h-16 rounded-full bg-teal-600/20 flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-teal-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Ask about this structure</h3>
              <p className="text-sm text-slate-400 mb-6">
                I can help you understand the protein chains, interpret scores, or explain structural features.
              </p>
              <div className="space-y-2 text-left w-full">
                <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Try asking:</p>
                {[
                  "What chains are in this structure?",
                  "Is this a full antibody or just fragments?",
                  "What do the scores tell me about binding?",
                  "How does this binder interact with the target?",
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setInputValue(suggestion);
                      inputRef.current?.focus();
                    }}
                    className="w-full text-left text-sm text-slate-300 hover:text-white bg-slate-700/50 hover:bg-slate-700 px-3 py-2 rounded-lg transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm ${
                      msg.role === "user"
                        ? "bg-teal-600 text-white rounded-br-md"
                        : "bg-slate-700 text-slate-100 rounded-bl-md"
                    }`}
                  >
                    {msg.role === "user" ? (
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                    ) : (
                      <div className="prose prose-sm prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-code:bg-slate-600 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-slate-200 prose-pre:bg-slate-800 prose-pre:my-2">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    )}
                    {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-slate-600 flex flex-wrap gap-1">
                        {msg.toolsUsed.map((tool) => (
                          <span
                            key={tool}
                            className="text-[10px] px-1.5 py-0.5 bg-slate-600 text-slate-300 rounded"
                          >
                            {formatToolName(tool)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {error && (
                <div className="flex justify-center">
                  <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm max-w-[90%]">
                    {error}
                  </div>
                </div>
              )}

              {isLoading && (messages.length === 0 || messages[messages.length - 1]?.role === "user") && (
                <div className="flex justify-start">
                  <div className="bg-slate-700 text-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span className="text-sm text-slate-400">
                        {activeTool ? `Using ${formatToolName(activeTool)}...` : "Thinking..."}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input area */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700 bg-slate-800/95 backdrop-blur">
          {!user ? (
            <div className="text-center py-2">
              <p className="text-sm text-slate-400 mb-3">Sign in to ask questions about this structure</p>
              <Link
                to={`/login?redirect=${encodeURIComponent(window.location.pathname)}`}
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Sign In
              </Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="flex items-end gap-2">
                <div className="flex-1">
                  <textarea
                    ref={inputRef}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask a question..."
                    disabled={isLoading}
                    rows={1}
                    className="w-full bg-slate-700 text-white px-4 py-2.5 rounded-lg border border-slate-600 focus:border-teal-500 focus:outline-none placeholder-slate-400 text-sm disabled:opacity-50 resize-none"
                    style={{ minHeight: "42px", maxHeight: "120px" }}
                  />
                </div>
                {isLoading ? (
                  <button
                    type="button"
                    onClick={cancel}
                    className="h-[42px] px-4 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Stop
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!inputValue.trim()}
                    className="h-[42px] px-4 bg-teal-600 hover:bg-teal-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Send
                  </button>
                )}
              </form>
              <p className="text-[10px] text-slate-500 mt-2 text-center">
                Press Enter to send, Shift+Enter for new line
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
